import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  BomIcon,
  AssemblyIcon,
  PartIcon,
  ChevronIcon,
  UnknownChildIcon,
  WhereUsedIcon,
  LocateIcon,
  DocumentIcon,
  EcoIcon,
} from './icons';
import Spinner from '../ui/Spinner';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { fieldTextFor } from '../utils/columns';
import {
  isCostColumn,
  formatCostValue,
  fmtCost,
  COST_COLUMN_KEYS,
  UNIT_COST_KEY,
  COST_KEY,
  TOTAL_COST_KEY,
} from '../utils/cost';
import { orcanosItemUrl } from '../api/orcanosClient';
import { getSettings } from '../settings/settingsStore';

const INDENT_PX = 24;
// How long the pointer must rest on a clipped cell before its tooltip opens.
const TIP_DELAY_MS = 450;

export default function BomRow({
  node,
  view = 'boms',
  columns,
  // { unit, qty, cost, total, complete } from the tree's cost rollup, or
  // undefined when the filter exposes no cost field.
  cost,
  // Called with the node when the user clicks ∑ in an unresolved Total Cost
  // cell — walks the row's whole sub-tree and fills in the exact figure.
  onCalcTotal,
  // True while that walk is in flight for this row.
  calculating = false,
  onToggle,
  onExpandAll,
  expandAllProgress,
  // Where-Used wiring. When `targetOriginalId` is set, we're viewing the
  // where-used results — show Locate on roots, hide WhereUsed everywhere.
  // Otherwise, show WhereUsed on non-BOM rows.
  targetOriginalId = null,
  onWhereUsed,
  onLocate,
  locating = false,
  located = false,
  // Hierarchical level number string, e.g. "1.2.3".
  levelNum = '',
  // Called with the node when the user picks Export from the ⋯ menu.
  // Available on EVERY row — an export starts from whatever item you pick and
  // walks that item's whole subtree, exactly as it does from a top-level BOM.
  onExport,
  // Called with the node when the user picks "Expand by category…" (root rows
  // only, same as ⤓). Undefined hides the item.
  onExpandByCategory,
  // True while this row's direct children are being fetched for the picker.
  categoryLoading = false,
  // Called with the node when the user picks Related ECOs from the ⋯ menu.
  onRelatedEcos,
  // Called with the node when the user picks Compare Revisions from the ⋯ menu.
  onCompare,
  // Called with (event, node) to open "Discuss with…" — from a RIGHT-CLICK anywhere on
  // the row, and from the ⋯ menu, because a right-click is not a gesture every user of a
  // read-only grid thinks to try. Both land on the same menu.
  onDiscuss,
}) {
  // Local open/close state for the ⋯ action menu.
  const [menuOpen, setMenuOpen] = useState(false);
  // Rich cell tooltip: null when hidden, { rect, html, hasImg } when visible.
  // `rect` is the anchor cell's viewport rect; the on-screen x/y is computed
  // AFTER the tooltip renders, from its real size (see the layout effect below).
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState(null);
  const tipRef = useRef(null);
  const hideTimer = useRef(null);
  // The tooltip opens only once the pointer RESTS on a cell. Opening it on every mouseenter
  // made a sweep across the grid pop a tooltip on each clipped cell it crossed and drop it
  // again one cell later — the whole grid flickered under the pointer.
  const showTimer = useRef(null);

  function cancelShow() {
    if (showTimer.current) { clearTimeout(showTimer.current); showTimer.current = null; }
  }
  function cancelHide() {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
  }
  function hideTip() {
    cancelShow();
    setTip((t) => (t === null ? t : null));
    setTipPos((p) => (p === null ? p : null));
  }
  function scheduleHide() {
    cancelShow();
    cancelHide();
    // Image tooltips have pointer-events:auto so the user can mouse into them —
    // give a grace period. Text tooltips are pointer-events:none; hide immediately.
    if (tip?.hasImg) {
      hideTimer.current = setTimeout(hideTip, 400);
    } else {
      hideTip();
    }
  }

  // Clean up timer on unmount.
  useEffect(() => () => { cancelHide(); cancelShow(); }, []);

  function handleCellEnter(e, value) {
    cancelHide();
    cancelShow();
    const raw = String(value);
    let hasImg = /<img\b/i.test(raw);

    if (hasImg) {
      // An image Orcanos refused to serve has already been swapped for a link
      // by the error handler in sanitizeHtml, so there may be no <img> left —
      // and blowing up a broken image into a preview shows nothing. Fall back
      // to the ordinary clipped-text rule in that case. A not-yet-loaded image
      // counts as usable so the first hover isn't swallowed.
      const imgs = e.currentTarget.querySelectorAll('img');
      hasImg = Array.from(imgs).some((im) => !im.complete || im.naturalWidth > 0);
    }

    if (!hasImg) {
      // Only show a tooltip when the content is actually clipped in the cell.
      const clip = e.currentTarget.querySelector('.bom-cell-clip');
      const truncated = clip && (
        clip.scrollWidth > clip.offsetWidth ||
        clip.scrollHeight > clip.offsetHeight
      );
      if (!truncated) { hideTip(); return; }
    }

    const cell = e.currentTarget;
    showTimer.current = setTimeout(() => {
      showTimer.current = null;
      if (!cell.isConnected) return;
      // Measured at open time, not at mouseenter — the grid may have scrolled while waiting.
      const r = cell.getBoundingClientRect();
      setTipPos(null);
      setTip({
        rect: { left: r.left, top: r.top, bottom: r.bottom, right: r.right },
        html: sanitizeHtml(raw),
        hasImg,
      });
    }, TIP_DELAY_MS);
  }

  // Position the tooltip from its ACTUAL size, not from the CSS max-width /
  // max-height. Guessing 500×500 for an image tooltip parked a small box far
  // from the cell it belonged to whenever the guess pushed it off-screen — and
  // an image that fails to load makes the box tiny. Re-runs on resize because
  // the image arrives after the tooltip mounts.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!tip || !el) return;

    const place = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const r = tip.rect;
      // Directly below the cell (4px gap) so the user can move straight down onto it.
      let x = r.left;
      let y = r.bottom + 4;
      if (y + h > window.innerHeight - 4) y = r.top - h - 4;          // flip above
      if (x + w > window.innerWidth - 4) x = window.innerWidth - w - 4; // shift left
      x = Math.max(4, x);
      y = Math.max(4, y);
      setTipPos((p) => (p && p.x === x && p.y === y ? p : { x, y }));
    };

    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tip]);

  // Clicking the image in the tooltip opens the full-size original in a new tab
  // (the tooltip clamps it to 484px).
  function handleTipClick(e) {
    const img = e.target?.closest?.('img');
    if (img?.src) window.open(img.src, '_blank', 'noopener,noreferrer');
  }

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);
  const isRoot = node.isRoot;

  // Children-state flags. `hasChildren` is set by the probe pass on every
  // row (top-level and child) so leaf detection works before any click.
  const knownLeaf = node.loaded && node.childCount === 0;
  const knownAssembly = node.loaded && node.childCount > 0;
  const probedLeaf = node.row.hasChildren === false;
  const probedAssembly = node.row.hasChildren === true;

  const isLeaf = knownLeaf || probedLeaf;
  const isAssembly = knownAssembly || probedAssembly;

  // Part Catalog: items whose Obj_name starts with one of the configured
  // BOM prefixes (`bomNamePrefixes` in settings.xml — defaults to
  // 50,51,52,53) render the BOM icon regardless of children state.
  const objName = String(node.row.objName || '').trimStart();
  const bomPrefixes =
    view === 'parts' ? getSettings().bomNamePrefixes || [] : [];
  const isBomByName = bomPrefixes.some(
    (p) => p && objName.startsWith(p)
  );

  // Icon selection. BOMs view: roots always render as BOM icons (they ARE
  // the BOM filter). Parts view: name-prefix rule wins, then assembly/leaf,
  // then unknown if we don't yet know.
  let iconNode;
  if (node.row.type === 'CR') iconNode = <EcoIcon />;
  else if (node.row.type === 'DMS' || view === 'dms') iconNode = <DocumentIcon />;
  else if (view === 'boms' && isRoot) iconNode = <BomIcon />;
  else if (isBomByName) iconNode = <BomIcon />;
  else if (isAssembly) iconNode = <AssemblyIcon />;
  else if (isLeaf) iconNode = <PartIcon />;
  else iconNode = <UnknownChildIcon />;

  // Chevron presence: hide it for leaves (rows we know have no children), and for an ECO
  // whose items are still being checked (`checking`, set by the hook's ECO check).
  const showChevron = !isLeaf && !node.row.checking;

  // ─── Where-Used / Locate button visibility ──────────────────────────────
  // Where-Used view (targetOriginalId set): show Locate on every root,
  //   never show WhereUsed (we'd be entering where-used while in where-used).
  // Normal view: show WhereUsed on any non-BOM row that has an originalId.
  const inWhereUsed = !!targetOriginalId;
  const isBom = (view === 'boms' && isRoot) || isBomByName;
  const showWhereUsed =
    !inWhereUsed && !isBom && !!node.row.originalId && !!onWhereUsed;
  const showLocate = inWhereUsed && isRoot && !!onLocate;

  // ─── Synthetic columns ──────────────────────────────────────
  // The normalizer extracts the canonical numeric ID from row.id (which the
  // API returns as e.g. "46584 (42195)"). The parenthetical is what
  // Orcanos's web URL expects.
  const itemId = node.row.itemId;
  // The row's own type when Orcanos gave one — an ECO's children are PRT and DMS, not PI.
  const linkType = node.row.type || (isRoot ? 'PRT' : 'PI');
  const linkHref = itemId ? orcanosItemUrl({ type: linkType, itemId }) : null;

  // For top-level BOMs, the API doesn't return Quantity / Master Part Source.
  // Per the source design doc:
  //   - quantity     → 1 for root BOMs
  //   - masterPart   → empty for root BOMs
  function valueFor(col) {
    // A merged column carries every Name/Title alias its work item types spell
    // the field with (see mergeRowColumns) — `fieldTextFor` tries them all, so
    // a PRT and a DMS item render under the same header.
    const colKey = typeof col === 'string' ? col : col?.key;
    if (colKey === '__quantity') {
      if (isRoot) return '1';
      return (
        node.row.byName('Quantity') ||
        node.row.byTitle('Quantity') ||
        ''
      );
    }
    if (colKey === '__masterPartSource') {
      if (isRoot) return '';
      return (
        node.row.byName('Master Part Source') ||
        node.row.byName('Master_Part_Source') ||
        node.row.byTitle('Master Part Source') ||
        ''
      );
    }
    if (colKey === '__revision') {
      // PRTs expose "Revision"; PIs expose "Part Revision". Show whichever
      // is present so both row types render under the same header.
      return (
        node.row.byTitle('Part Revision') ||
        node.row.byName('Part Revision') ||
        node.row.byTitle('Revision') ||
        node.row.byName('Revision') ||
        ''
      );
    }
    if (typeof col === 'object' && col) return fieldTextFor(node.row, col);
    return node.row.byName(colKey) || node.row.byTitle(colKey) || '';
  }

  // The "Key" column (Name = User_Prefix, Title = "Key") is rendered as a
  // hyperlink to the matching Orcanos record. Detect it here so the column
  // logic stays generic.
  function isKeyColumn(c) {
    return (
      c.key === 'User_Prefix' ||
      String(c.title || '').trim().toLowerCase() === 'key'
    );
  }

  // ⋯ menu: Export and Related ECOs on any row; "Expand by category…" only
  // where ⤓ lives (roots), since it is that button's filtered twin.
  const showExpandByCategory = isRoot && !isLeaf && !!onExpandByCategory;
  const showMenu =
    !!onExport || !!onRelatedEcos || !!onCompare || !!onDiscuss || showExpandByCategory;

  return (
    <tr
      data-node-key={node.nodeKey}
      className={
        'bom-row' +
        (node.errored ? ' bom-row--errored' : '') +
        (isRoot ? ' bom-row--root' : '') +
        (located ? ' bom-row--located' : '')
      }
      onMouseLeave={scheduleHide}
      onContextMenu={onDiscuss ? (e) => onDiscuss(e, node) : undefined}
    >
      {/* Tree column ─ level number + chevron + icon + indentation */}
      <td className="bom-col-tree">
        <div
          className="bom-cell-tree"
          style={{ paddingLeft: node.depth * INDENT_PX + 'px' }}
        >
          {levelNum && !isRoot && (
            <span className="bom-level-num" title={`Level ${levelNum}`}>
              {levelNum}
            </span>
          )}

          <button
            type="button"
            className="bom-chevron"
            onClick={onToggle}
            aria-label={node.expanded ? 'Collapse' : 'Expand'}
            disabled={!showChevron || node.loading}
            style={{
              visibility: showChevron ? 'visible' : 'hidden',
            }}
          >
            {node.loading ? <Spinner size={12} /> : <ChevronIcon open={node.expanded} />}
          </button>

          <span className="bom-icon">{iconNode}</span>

          {showWhereUsed && (
            <button
              type="button"
              className="bom-row-action-btn"
              onClick={() => onWhereUsed(node)}
              title={`Where used — find BOMs that contain "${objName}"`}
              aria-label="Where used"
            >
              <WhereUsedIcon />
            </button>
          )}

          {showLocate && (
            <button
              type="button"
              className="bom-row-action-btn bom-row-action-btn--locate"
              onClick={() => onLocate(node)}
              disabled={locating}
              title="Locate the part inside this BOM"
              aria-label="Locate"
            >
              {locating ? <Spinner size={12} /> : <LocateIcon />}
            </button>
          )}

          {isRoot && !isLeaf && (
            <button
              type="button"
              className="bom-expand-all-btn"
              onClick={onExpandAll}
              title="Expand all descendants"
            >
              ⤓
              {expandAllProgress && (
                <span className="bom-expand-all-progress">
                  {expandAllProgress.done}/{expandAllProgress.total}
                </span>
              )}
            </button>
          )}

          {showMenu && (
            <div className="bom-row-menu-wrap">
              <button
                type="button"
                className="bom-row-action-btn bom-row-menu-btn"
                title="More actions"
                aria-label="More actions"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
              >
                ···
              </button>
              {menuOpen && (
                <div className="bom-row-menu" onClick={(e) => e.stopPropagation()}>
                  {showExpandByCategory && (
                    <button
                      type="button"
                      className="bom-row-menu-item"
                      disabled={categoryLoading}
                      onClick={() => { setMenuOpen(false); onExpandByCategory(node); }}
                    >
                      {categoryLoading ? 'Loading…' : 'Expand by category…'}
                    </button>
                  )}
                  {onExport && (
                    <button
                      type="button"
                      className="bom-row-menu-item"
                      onClick={() => { setMenuOpen(false); onExport(node); }}
                    >
                      Export
                    </button>
                  )}
                  {onRelatedEcos && (
                    <button
                      type="button"
                      className="bom-row-menu-item"
                      onClick={() => { setMenuOpen(false); onRelatedEcos(node); }}
                    >
                      Related ECOs
                    </button>
                  )}
                  {onCompare && (
                    <button
                      type="button"
                      className="bom-row-menu-item"
                      onClick={() => { setMenuOpen(false); onCompare(node); }}
                    >
                      Compare Revisions
                    </button>
                  )}
                  {onDiscuss && (
                    <button
                      type="button"
                      className="bom-row-menu-item"
                      onClick={(e) => { setMenuOpen(false); onDiscuss(e, node); }}
                    >
                      Discuss…
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </td>

      {/* Rich hover tooltip — rendered via portal so it escapes table overflow.
          The portal leaves the module's DOM subtree, so it carries its own `.bom-scope`
          wrapper: every BOM style is prefixed with that class, and without it the
          tooltip renders unstyled (and its images skip the broken-image fallback). */}
      {tip && createPortal(
        <div className="bom-scope">
        <div
          ref={tipRef}
          className={'cell-tooltip' + (tip.hasImg ? ' cell-tooltip--image' : '')}
          style={{
            left: tipPos ? tipPos.x : tip.rect.left,
            top: tipPos ? tipPos.y : tip.rect.bottom + 4,
            visibility: tipPos ? 'visible' : 'hidden',
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={hideTip}
          onClick={handleTipClick}
        >
          {tip.hasImg && (
            <button className="cell-tooltip-close" onClick={hideTip} title="Close">✕</button>
          )}
          <div dangerouslySetInnerHTML={{ __html: tip.html }} />
        </div>
        </div>,
        document.body
      )}

      {/* Dynamic + synthetic columns */}
      {columns.map((c) => {
        // Cost columns are computed by the tree's rollup, not read off the row.
        if (COST_COLUMN_KEYS.has(c.key)) {
          const n =
            c.key === UNIT_COST_KEY ? cost?.unit
            : c.key === COST_KEY ? cost?.cost
            : cost?.total;

          // Until every descendant has been seen, a rolled-up total is only a
          // floor — showing it would read as the BOM's cost when it is a
          // fraction of it. So we show NO number and offer to go get the real
          // one instead. Unit Cost and Cost are per-row and always exact.
          const unresolved =
            c.key === TOTAL_COST_KEY && cost && !cost.complete && !!onCalcTotal;

          if (unresolved) {
            return (
              <td key={c.key} className="bom-col-cost">
                <button
                  type="button"
                  className="bom-cost-calc"
                  onClick={() => onCalcTotal(node)}
                  disabled={calculating}
                  title={`Total the whole sub-tree under "${objName}". Fetches every level below this row.`}
                  aria-label="Calculate total cost for this sub-tree"
                >
                  {calculating ? <Spinner size={11} /> : '∑'}
                </button>
              </td>
            );
          }

          return (
            <td key={c.key} className="bom-col-cost">
              {n ? fmtCost(n) : ''}
            </td>
          );
        }

        // ECO view: the file revision this ECO released for a DMS item. The link opens in
        // Orcanos on the user's own login — nothing is downloaded through this app.
        if (c.key === '__file') {
          const f = node.row.ecoFile;
          return (
            <td key={c.key} className="bom-col-text">
              {f ? (
                <span className="bom-file-cell">
                  <a href={f.url} target="_blank" rel="noopener noreferrer" title={`Open ${f.name} in Orcanos`}>
                    {f.name}
                  </a>
                  <span className="bom-file-rev">rev {f.revision}{f.changeNo ? `.${f.changeNo}` : ''}</span>
                </span>
              ) : node.row.ecoFileError ? (
                <span className="bom-file-error" title={node.row.ecoFileError}>Couldn&rsquo;t read revisions</span>
              ) : null}
            </td>
          );
        }

        const raw = valueFor(c);
        const value = isCostColumn(c) ? formatCostValue(raw) : raw;
        const tooltip = String(value).replace(/<[^>]+>/g, '').trim();

        if (isKeyColumn(c) && linkHref) {
          return (
            <td key={c.key} className="bom-col-key" title={tooltip}>
              <a
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(value),
                }}
              />
            </td>
          );
        }

        return (
          <td
            key={c.key}
            className="bom-col-text"
            onMouseEnter={(e) => handleCellEnter(e, value)}
            onMouseLeave={scheduleHide}
          >
            <div className="bom-cell-clip">
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />
            </div>
          </td>
        );
      })}
    </tr>
  );
}
